/**
 * Preset list with drag reorder and optional tag-selection mode
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { useCallback, useMemo, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragCancelEvent,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';

import type { PreparedDrop } from '@/features/smswiser-campaigns/types/workspace.types';

import type { Preset } from '../shared';
import { PresetGroupSection } from './PresetGroupSection';
import { PresetGroupDragOverlay } from './PresetGroupDragOverlay';
import { PresetRowDragOverlay } from './PresetRowDragOverlay';
import { SortablePresetRow } from './SortablePresetRow';
import { sumPresetTemplateDrops } from './shared';
import { anyPresetNeedsBulkRouting } from '../shared/preset-bulk-apply-routing.utils';
import {
  buildFlattenedSortableIds,
  computeNewPresetOrderAfterDrag,
  firstPresetIdFromGroupStartSortableId,
  groupPresetsContiguous,
} from './sortable';

export interface SortablePresetListProps {
  presets: Preset[];
  tagColors?: Record<string, string>;
  selectedDrafts?: PreparedDrop[];
  isLoading: boolean;
  onApply: (preset: Preset) => void;
  onEdit: (preset: Preset) => void;
  onDelete: (preset: Preset) => void;
  onAddToPreset?: (preset: Preset, drops: PreparedDrop[]) => void;
  isApplying?: boolean;
  isBusy?: boolean;
  selectionMode?: boolean;
  selectedIds?: Set<string>;
  onToggleSelected?: (id: string) => void;
  onReorder: (orderedIds: string[]) => void;
  onApplyGroup?: (presetIds: string[], matchListOrder: boolean) => void;
  bulkMatchListOrder?: boolean;
  bulkSelected?: Set<string>;
  onToggleGroupBulk?: (presetIds: string[]) => void;
  /** Campaign chosen in bulk bar. */
  bulkCampaignOk?: boolean;
  /** Gateway + TLD valid when any rule-based preset lacks them on the template. */
  bulkRoutingFilled?: boolean;
}

export const SortablePresetList: FC<SortablePresetListProps> = ({
  presets,
  tagColors,
  selectedDrafts = [],
  isLoading,
  onApply,
  onEdit,
  onDelete,
  onAddToPreset,
  isApplying = false,
  isBusy = false,
  selectionMode = false,
  selectedIds,
  onToggleSelected,
  onReorder,
  onApplyGroup,
  bulkMatchListOrder = false,
  bulkSelected,
  onToggleGroupBulk,
  bulkCampaignOk = true,
  bulkRoutingFilled = true,
}) => {
  const groups = useMemo(() => groupPresetsContiguous(presets), [presets]);
  const [expandedFirstIds, setExpandedFirstIds] = useState<Set<string>>(() => new Set());
  const [activeDragPresetId, setActiveDragPresetId] = useState<string | null>(null);
  const [activeDragGroupFirstPresetId, setActiveDragGroupFirstPresetId] = useState<string | null>(null);

  const toggleGroup = useCallback((firstPresetId: string) => {
    setExpandedFirstIds((prev) => {
      const next = new Set(prev);
      if (next.has(firstPresetId)) next.delete(firstPresetId);
      else next.add(firstPresetId);
      return next;
    });
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const flatIds = useMemo(() => buildFlattenedSortableIds(presets), [presets]);

  const handleDragStart = (e: DragStartEvent) => {
    const id = String(e.active.id);
    const groupFirst = firstPresetIdFromGroupStartSortableId(id);
    if (groupFirst != null) {
      setActiveDragGroupFirstPresetId(groupFirst);
      setActiveDragPresetId(null);
      return;
    }
    setActiveDragGroupFirstPresetId(null);
    setActiveDragPresetId(id);
  };

  const handleDragCancel = (_e?: DragCancelEvent) => {
    setActiveDragPresetId(null);
    setActiveDragGroupFirstPresetId(null);
  };

  const handleDragEnd = (e: DragEndEvent) => {
    setActiveDragPresetId(null);
    setActiveDragGroupFirstPresetId(null);
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const next = computeNewPresetOrderAfterDrag(presets, String(active.id), String(over.id));
    if (next == null) return;
    onReorder(next);
  };

  const canAdd = onAddToPreset != null && selectedDrafts.length > 0;
  const applyBusy = isApplying || isBusy;
  const activePreset = activeDragPresetId
    ? presets.find((p) => p.id === activeDragPresetId)
    : undefined;
  const activeDragGroup =
    activeDragGroupFirstPresetId != null
      ? groups.find((g) => g.firstPresetId === activeDragGroupFirstPresetId)
      : undefined;

  if (isLoading) return <div className="py-8 text-center text-sm text-muted-foreground">Loading presets...</div>;
  if (presets.length === 0)
    return (
      <div className="py-8 text-center text-sm text-muted-foreground">
        No presets yet. Create one from prepared drops or from scratch.
      </div>
    );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <SortableContext items={flatIds} strategy={verticalListSortingStrategy}>
        <div className="flex flex-col gap-3">
          {groups.map((g) => {
            const groupNeedsRouting = anyPresetNeedsBulkRouting(g.items);
            const groupApplyBlocked =
              !bulkCampaignOk || (groupNeedsRouting && !bulkRoutingFilled);
            return (
              <PresetGroupSection
                key={g.firstPresetId}
                group={g}
                expanded={expandedFirstIds.has(g.firstPresetId)}
                onToggleExpand={() => toggleGroup(g.firstPresetId)}
                onApplyGroup={onApplyGroup}
                bulkMatchListOrder={bulkMatchListOrder}
                applyDisabled={applyBusy || groupApplyBlocked}
                groupNeedsRouting={groupNeedsRouting}
                bulkCampaignOk={bulkCampaignOk}
                bulkRoutingFilled={bulkRoutingFilled}
                selectionMode={selectionMode}
                bulkSelected={bulkSelected}
                onToggleGroupBulk={onToggleGroupBulk}
              >
                {g.items.map((preset) => (
                  <SortablePresetRow
                    key={preset.id}
                    preset={preset}
                    tagColors={tagColors}
                    selectedDrafts={selectedDrafts}
                    canAddToPreset={canAdd}
                    onApply={() => onApply(preset)}
                    onEdit={() => onEdit(preset)}
                    onDelete={() => onDelete(preset)}
                    onAddToPreset={canAdd ? () => onAddToPreset?.(preset, selectedDrafts) : undefined}
                    isApplying={isApplying}
                    isBusy={isBusy}
                    selectionMode={selectionMode}
                    selected={selectedIds?.has(preset.id) ?? true}
                    onToggleSelected={() => onToggleSelected?.(preset.id)}
                  />
                ))}
              </PresetGroupSection>
            );
          })}
        </div>
      </SortableContext>
      <DragOverlay dropAnimation={null}>
        {activePreset ? (
          <PresetRowDragOverlay
            preset={activePreset}
            tagColors={tagColors}
            selectedDrafts={selectedDrafts}
            canAddToPreset={canAdd}
            selectionMode={selectionMode}
            selected={selectedIds?.has(activePreset.id) ?? true}
          />
        ) : activeDragGroup ? (
          <PresetGroupDragOverlay
            label={activeDragGroup.label}
            templateDropCount={sumPresetTemplateDrops(activeDragGroup.items)}
            presetCount={activeDragGroup.items.length}
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};
